import { useAppDispatch, useAppSelector } from '@verzekeringapp-mono/shared/src/hooks/state'
import { logout } from '@verzekeringapp-mono/shared/src/state/actions/constants'
import { getResourceAuthorizations, getResourceAuthorization } from '@verzekeringapp-mono/shared/src/state/selectors/resource_authorization'
import { getTerms } from '@verzekeringapp-mono/shared/src/state/selectors/terms'
import { getUser, getToken } from '@verzekeringapp-mono/shared/src/state/selectors/user'
import { FC, PropsWithChildren, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useLocation, Navigate } from 'react-router-dom'
import * as Sentry from '@sentry/react'

export const ProtectedRoute: FC<PropsWithChildren> = ({ children }) => {
  const user = useSelector(getUser)
  const dispatch = useAppDispatch()
  const resourceAuthorization = useAppSelector(getResourceAuthorization)
  const resourceAuthorizations = useAppSelector(getResourceAuthorizations)
  const location = useLocation()
  const token = useAppSelector(getToken)
  const terms = useAppSelector(getTerms)

  const termsAcceptedAt = useMemo(() => new Date(terms?.acceptedAt ?? 0), [terms?.acceptedAt])
  const termsActiveSince = useMemo(() => terms?.activeSince ? new Date(terms.activeSince) : new Date(0), [terms?.activeSince])
  const termsAccepted = useMemo(() => !terms || termsAcceptedAt.getTime() >= termsActiveSince.getTime(), [terms, termsAcceptedAt, termsActiveSince])
  const newResourcesFound = useMemo(() => !resourceAuthorization?.seenAt || resourceAuthorizations.some(ra => !ra.seenAt), [resourceAuthorization, resourceAuthorizations])
  const allResourceAuthorizationsInactive = useMemo(() => !resourceAuthorization?.active && resourceAuthorizations.every(ra => !ra.active), [resourceAuthorization, resourceAuthorizations])

  if (!user || !token) {
    Sentry.addBreadcrumb({
      category: 'auth',
      level: 'warning',
      message: 'Automatic logout dispatched',
      data: {
        reason: 'web_protected_route_missing_auth',
        source: 'ProtectedRoute',
        hasUser: Boolean(user),
        hasToken: Boolean(token),
        pathname: location.pathname
      }
    })
    dispatch(logout())

    return <Navigate to='/home' />
  }

  if (
    (!termsAccepted || !resourceAuthorization || newResourcesFound || allResourceAuthorizationsInactive) &&
    location.pathname !== '/instellen'
  ) {
    return <Navigate to='/instellen' />
  }

  return children
}
